// frontend/src/components/ArchivePanel.tsx
// Раздел «Архив» в админ-панели — удалённые пользователи и компании

import React, { useEffect, useState } from 'react';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';

interface ArchiveItem {
  _id: string;
  entityType: 'user' | 'company';
  name?: string;
  email?: string;
  role?: string;
  companyName?: string;
  registeredAt?: string;
  lastActive?: string;
  lastLogin?: string;
  paid?: boolean;
  paymentStatus?: string;
  tariffPlan?: string;
  deletedAt: string;
  deletedBy?: string;
  reason?: string;
}

interface Stats {
  usersTotal: number;
  companiesTotal: number;
  manualDeletes: number;
}

// ⭐ Состояние модалки подтверждения
interface ConfirmState {
  open: boolean;
  title: string;
  message: string;
  details?: string;
  count?: number;
  onConfirm: () => void;
}

const EMPTY_CONFIRM: ConfirmState = {
  open: false,
  title: '',
  message: '',
  onConfirm: () => {},
};


const ArchivePanel: React.FC = () => {
  const { user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';

  const [type, setType] = useState<'user' | 'company'>('user');
  const [items, setItems] = useState<ArchiveItem[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [reason, setReason] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<ConfirmState>(EMPTY_CONFIRM);
  const [deleting, setDeleting] = useState(false);

  const closeConfirm = () => {
    if (deleting) return;
    setConfirm(EMPTY_CONFIRM);
  };

  // Escape закрывает модалку
  useEffect(() => {
    if (!confirm.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeConfirm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirm.open, deleting]);

  const fetchStats = async () => {
    try {
      const res = await api.get('/archive/stats');
      setStats(res.data);
    } catch (e) {
      console.error('stats error', e);
    }
  };

  const fetchItems = async (p = page) => {
    setLoading(true);
    try {
      const params: any = { type, page: p, limit: 50 };
      if (search.trim()) params.search = search.trim();
      if (reason) params.reason = reason;
      const res = await api.get('/archive', { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
    } catch (e) {
      console.error('archive error', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    setPage(1);
    fetchItems(1);
  }, [type, reason]);

  const handleSearch = () => {
    setPage(1);
    fetchItems(1);
  };

  const handleExport = () => {
    const params = new URLSearchParams({ type });
    if (search.trim()) params.set('search', search.trim());
    if (reason) params.set('reason', reason);
    // Скачиваем через window.location (с cookie/token)
    const url = `/api/archive/export?${params.toString()}`;
    window.open(url, '_blank');
  };

  const handleDeleteOne = (item: ArchiveItem) => {
    const label = item.email || item.companyName || item.name || item._id;
    setConfirm({
      open: true,
      title: item.entityType === 'company' ? 'Удалить компанию из архива?' : 'Удалить пользователя из архива?',
      message: 'Запись будет удалена безвозвратно.',
      details: label,
      onConfirm: async () => {
        setDeleting(true);
        try {
          await api.delete(`/archive/${item._id}`);
          setItems(prev => prev.filter(x => x._id !== item._id));
          setTotal(prev => Math.max(0, prev - 1));
          fetchStats();
          setConfirm(EMPTY_CONFIRM);
        } catch (e: any) {
          alert(e?.response?.data?.error || 'Не удалось удалить');
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    setConfirm({
      open: true,
      title: `Удалить ${ids.length} ${plural(ids.length, 'запись', 'записи', 'записей')}?`,
      message: 'Все выбранные записи будут удалены из архива безвозвратно.',
      count: ids.length,
      onConfirm: async () => {
        setDeleting(true);
        try {
          const res = await api.post('/archive/bulk-delete', { ids });
          setSelectedIds(new Set());
          fetchItems(page);
          fetchStats();
          setConfirm(EMPTY_CONFIRM);
          setTimeout(() => alert(`✅ ${res.data.message}`), 100);
        } catch (e: any) {
          alert(e?.response?.data?.error || 'Не удалось удалить');
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map(i => i._id)));
  };

  const formatDate = (d?: string) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const reasonLabel = (r?: string) => {
    switch (r) {
      case 'manual_delete': return '🗑️ Удалён вручную';
      case 'bulk_delete': return '📦 Массовое удаление';
      case 'company_delete': return '🏢 Удаление компании';
      default: return r || '—';
    }
  };

  const allSelected = items.length > 0 && selectedIds.size === items.length;

  return (
    <div>
      {/* Заголовок + статистика */}
      <div style={styles.header}>
        <h3 style={{ margin: 0 }}>📦 Архив</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { fetchStats(); fetchItems(); }} style={styles.refreshButton} disabled={loading}>
            🔄 Обновить
          </button>
          <button onClick={handleExport} style={styles.exportButton}>
            📥 Экспорт в Excel
          </button>
        </div>
      </div>

      {stats && (
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.usersTotal}</div>
            <div style={styles.statLabel}>Пользователей</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.companiesTotal}</div>
            <div style={styles.statLabel}>Компаний</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.manualDeletes}</div>
            <div style={styles.statLabel}>Вручную</div>
          </div>
        </div>
      )}

      {/* Табы Пользователи / Компании */}
      <div style={styles.subTabs}>
        <button
          style={{ ...styles.subTab, ...(type === 'user' ? styles.subTabActive : {}) }}
          onClick={() => setType('user')}
        >
          👤 Пользователи
        </button>
        <button
          style={{ ...styles.subTab, ...(type === 'company' ? styles.subTabActive : {}) }}
          onClick={() => setType('company')}
        >
          🏢 Компании
        </button>
      </div>

      {/* Фильтры */}
      <div style={styles.filters}>
        <input
          type="text"
          placeholder="Поиск по имени, email, компании..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          style={styles.searchInput}
        />
        <select value={reason} onChange={e => setReason(e.target.value)} style={styles.filterSelect}>
          <option value="">Все причины</option>
          <option value="manual_delete">Удалён вручную</option>
          <option value="bulk_delete">Массовое удаление</option>
        </select>
        <button onClick={handleSearch} style={styles.searchButton} disabled={loading}>
          🔍 Найти
        </button>
      </div>

      {/* Таблица */}
      {isSuperadmin && selectedIds.size > 0 && (
        <div style={styles.bulkBar}>
          <span style={{ fontSize: 13, color: '#ff4d4f', fontWeight: 600 }}>Выбрано: {selectedIds.size}</span>
          <button onClick={handleBulkDelete} style={styles.bulkDeleteButton}>🗑 Удалить выбранные безвозвратно</button>
          <button onClick={() => setSelectedIds(new Set())} style={styles.bulkCancelButton}>Отменить выбор</button>
        </div>
      )}

      {loading ? (
        <p style={{ textAlign: 'center', padding: 40, color: '#666' }}>Загрузка...</p>
      ) : items.length === 0 ? (
        <div style={styles.empty}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
          <p>Архив пуст</p>
          <p style={{ fontSize: 13, color: '#999' }}>
            Здесь появятся удалённые пользователи и компании
          </p>
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto', marginTop: 12 }}>
            {type === 'user' ? (
              <table style={styles.table}>
                <thead>
                  <tr>
                    {isSuperadmin && (
                      <th style={{ ...styles.th, width: 40 }}>
                        <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} style={{ cursor: 'pointer' }} />
                      </th>
                    )}
                    <th style={styles.th}>Имя</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Роль</th>
                    <th style={styles.th}>Компания</th>
                    <th style={styles.th}>Регистрация</th>
                    <th style={styles.th}>Последняя активность</th>
                    <th style={styles.th}>Удалён</th>
                    <th style={styles.th}>Причина</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => (
                    <tr key={it._id} style={styles.tr}>
                      {isSuperadmin && (
                        <td style={styles.td}>
                          <input type="checkbox" checked={selectedIds.has(it._id)}
                            onChange={() => toggleSelect(it._id)} style={{ cursor: 'pointer' }} />
                        </td>
                      )}
                      <td style={styles.td}>{it.name || '—'}</td>
                      <td style={styles.td}>{it.email || '—'}</td>
                      <td style={styles.td}>{it.role || '—'}</td>
                      <td style={styles.td}>{it.companyName || '—'}</td>
                      <td style={styles.td}>{formatDate(it.registeredAt)}</td>
                      <td style={styles.td}>{formatDate(it.lastActive)}</td>
                      <td style={styles.td}>{formatDate(it.deletedAt)}</td>
                      <td style={styles.td}>{reasonLabel(it.reason)}</td>
                      {isSuperadmin && (
                        <td style={styles.td}>
                          <button onClick={() => handleDeleteOne(it)} style={styles.deleteIconButton} title="Удалить безвозвратно">🗑</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    {isSuperadmin && (
                      <th style={{ ...styles.th, width: 40 }}>
                        <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} style={{ cursor: 'pointer' }} />
                      </th>
                    )}
                    <th style={styles.th}>Компания</th>
                    <th style={styles.th}>Дата регистрации</th>
                    <th style={styles.th}>Удалена</th>
                    <th style={styles.th}>Причина</th>
                    {isSuperadmin && <th style={{ ...styles.th, width: 60 }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => (
                    <tr key={it._id} style={styles.tr}>
                      {isSuperadmin && (
                        <td style={styles.td}>
                          <input type="checkbox" checked={selectedIds.has(it._id)}
                            onChange={() => toggleSelect(it._id)} style={{ cursor: 'pointer' }} />
                        </td>
                      )}
                      <td style={styles.td}>{it.companyName || it.name || '—'}</td>
                      <td style={styles.td}>{formatDate(it.registeredAt)}</td>
                      <td style={styles.td}>{formatDate(it.deletedAt)}</td>
                      <td style={styles.td}>{reasonLabel(it.reason)}</td>
                      {isSuperadmin && (
                        <td style={styles.td}>
                          <button onClick={() => handleDeleteOne(it)} style={styles.deleteIconButton} title="Удалить безвозвратно">🗑</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Пагинация */}
          {totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                onClick={() => { const p = Math.max(1, page - 1); setPage(p); fetchItems(p); }}
                disabled={page <= 1 || loading}
                style={styles.pageButton}
              >
                ← Назад
              </button>
              <span style={{ fontSize: 14, color: '#666' }}>
                Страница {page} из {totalPages} (всего: {total})
              </span>
              <button
                onClick={() => { const p = Math.min(totalPages, page + 1); setPage(p); fetchItems(p); }}
                disabled={page >= totalPages || loading}
                style={styles.pageButton}
              >
                Вперёд →
              </button>
            </div>
          )}
        </>
      )}
      {/* ⭐ МОДАЛЬНОЕ ОКНО ПОДТВЕРЖДЕНИЯ */}
      {confirm.open && (
        <div style={styles.overlay} onClick={closeConfirm}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <button onClick={closeConfirm} disabled={deleting} style={styles.modalClose} title="Закрыть (Esc)">×</button>
            <div style={styles.modalIcon}>⚠️</div>
            <h3 style={styles.modalTitle}>{confirm.title}</h3>
            <p style={styles.modalMessage}>{confirm.message}</p>
            {confirm.details && (
              <div style={styles.modalDetails}>{confirm.details}</div>
            )}
            <div style={styles.modalWarning}>
              Данные будут стёрты из базы навсегда.
              Восстановление невозможно (152-ФЗ).
            </div>
            <div style={styles.modalActions}>
              <button onClick={closeConfirm} disabled={deleting} style={styles.modalCancelButton}>Отмена</button>
              <button
                onClick={confirm.onConfirm}
                disabled={deleting}
                style={{ ...styles.modalConfirmButton, opacity: deleting ? 0.7 : 1, cursor: deleting ? 'wait' : 'pointer' }}
              >
                {deleting ? (<><span style={styles.spinner}></span>Удаление...</>) : (<>🗑 Удалить безвозвратно</>)}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// ==================== Стили ====================
const styles: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 16, flexWrap: 'wrap', gap: 8,
  },
  refreshButton: {
    padding: '8px 14px', borderRadius: 10, border: '1px solid #ddd',
    background: '#fff', cursor: 'pointer', fontSize: 13,
  },
  exportButton: {
    padding: '8px 14px', borderRadius: 10, border: 'none',
    background: '#28a745', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },
  statsRow: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 12, marginBottom: 20,
  },
  statCard: {
    padding: '16px 18px', borderRadius: 14,
    background: 'linear-gradient(135deg, #ede9fe, #fce7f3)',
    boxShadow: '0 2px 8px rgba(139, 92, 246, 0.08)',
  },
  statValue: { fontSize: 26, fontWeight: 700, color: '#7c3aed' },
  statLabel: { fontSize: 12, color: '#666', marginTop: 4, textTransform: 'uppercase', letterSpacing: 0.5 },

  subTabs: { display: 'flex', gap: 8, marginBottom: 16 },
  subTab: {
    padding: '10px 18px', borderRadius: 12, border: '1px solid #ddd',
    background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500,
  },
  subTabActive: {
    background: '#8b5cf6', color: '#fff', borderColor: '#8b5cf6',
    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
  },

  filters: {
    display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap',
  },
  searchInput: {
    flex: 1, minWidth: 200, padding: '10px 14px',
    borderRadius: 10, border: '1px solid #ddd', fontSize: 14,
  },
  filterSelect: {
    padding: '10px 14px', borderRadius: 10, border: '1px solid #ddd',
    fontSize: 14, background: '#fff', cursor: 'pointer',
  },
  searchButton: {
    padding: '10px 18px', borderRadius: 10, border: 'none',
    background: '#8b5cf6', color: '#fff', cursor: 'pointer',
    fontSize: 14, fontWeight: 600,
  },

  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '12px 10px',
    background: '#f5f3ff', color: '#666', fontWeight: 600,
    fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5,
    borderBottom: '2px solid #ede9fe',
  },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '12px 10px', color: '#333' },

  empty: {
    textAlign: 'center', padding: '60px 20px', color: '#666',
  },
  pagination: {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    gap: 12, marginTop: 20, flexWrap: 'wrap',
  },
  pageButton: {
    padding: '8px 14px', borderRadius: 10, border: '1px solid #ddd',
    background: '#fff', cursor: 'pointer', fontSize: 13,
  },
  // ⭐ Стили модалки и панели массовых действий
  bulkBar: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', marginBottom: 12, background: 'rgba(255, 77, 79, 0.08)', border: '1px solid rgba(255, 77, 79, 0.3)', borderRadius: 12, flexWrap: 'wrap' },
  bulkDeleteButton: { padding: '8px 14px', borderRadius: 10, border: 'none', background: '#ff4d4f', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  bulkCancelButton: { padding: '8px 14px', borderRadius: 10, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13 },
  deleteIconButton: { border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, padding: 4, borderRadius: 6, opacity: 0.6 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20, animation: 'fadeIn 0.18s ease-out' },
  modal: { position: 'relative', background: '#fff', borderRadius: 20, padding: '32px 28px 24px', maxWidth: 460, width: '100%', boxShadow: '0 24px 60px rgba(15, 23, 42, 0.35), 0 4px 16px rgba(15, 23, 42, 0.15)', textAlign: 'center', animation: 'popIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)', border: '1px solid rgba(255, 77, 79, 0.12)' },
  modalClose: { position: 'absolute', top: 12, right: 12, width: 32, height: 32, border: 'none', background: '#f5f3ff', color: '#666', borderRadius: 10, fontSize: 20, lineHeight: 1, cursor: 'pointer' },
  modalIcon: { fontSize: 48, marginBottom: 8, lineHeight: 1, filter: 'drop-shadow(0 4px 10px rgba(255, 77, 79, 0.35))' },
  modalTitle: { fontSize: 20, fontWeight: 700, color: '#1f2937', margin: '0 0 10px' },
  modalMessage: { fontSize: 14, color: '#4b5563', margin: '0 0 14px', lineHeight: 1.5 },
  modalDetails: { display: 'inline-block', padding: '10px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, fontSize: 14, color: '#991b1b', fontWeight: 600, marginBottom: 16, maxWidth: '100%', wordBreak: 'break-word' },
  modalWarning: { fontSize: 12, color: '#b91c1c', background: 'rgba(255, 77, 79, 0.08)', padding: '10px 12px', borderRadius: 10, marginBottom: 22, lineHeight: 1.5 },
  modalActions: { display: 'flex', gap: 10, justifyContent: 'center' },
  modalCancelButton: { padding: '12px 22px', borderRadius: 12, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 14, fontWeight: 600, minWidth: 110 },
  modalConfirmButton: { padding: '12px 22px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #ff4d4f, #d9363e)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700, boxShadow: '0 6px 16px rgba(255, 77, 79, 0.4)', display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 200, justifyContent: 'center' },
  spinner: { display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },

};


function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

if (typeof document !== 'undefined' && !document.getElementById('archive-modal-anim')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'archive-modal-anim';
  styleEl.textContent = `
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes popIn {
      from { opacity: 0; transform: scale(0.92) translateY(8px); }
      to   { opacity: 1; transform: scale(1) translateY(0); }
    }
    @keyframes spin { to { transform: rotate(360deg); } }
  `;
  document.head.appendChild(styleEl);
}

export default ArchivePanel;
